<template>
  <div>
    <transition v-if="!loaded" mode="out-in" name="fade">
      <Loader />
    </transition>
    <transition
      v-else-if="canDisplay"
      v-visible="displayContent"
      mode="out-in"
      name="fade"
    >
      <div>
        <App />
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  PropType,
  watch,
  Ref,
  watchEffect
} from "vue";

import { provideBetData } from "@fresh8/ad-data-composition/src/betData";

import {
  provideBrand,
  provideAdTag
} from "@fresh8/ad-data-composition/src/brand";

import { useGetPublisherBetData } from "./hooks/useGetBetData";
import { Defaults as potentialWinningsDefaults } from "@fresh8/ad-data-composition/src/usePotentialWinnings";

import {
  AdTag,
  AdTagExperimentVariant,
  BackgroundImageSetMap
} from "@fresh8/types/src/index";
import { provideTheme } from "@fresh8/ad-data-composition/src/theme";
import { providePreview } from "@fresh8/ad-data-composition/src/preview";
import { providePreroll } from "./hooks/usePreroll";

import App from "./App.vue";
import Loader from "./components/Loader.vue";
import { createJssContext, provideJssContext } from "@fresh8/vue-jss/src";
import {
  provideAnalyticsContext,
  createAnalyticsContext
} from "./lib/analytics";
import {
  provideCardAnalyticsContext,
  createCardAnalyticsContext
} from "./lib/card-analytics";
import { Preview } from "@fresh8/types/src/preview";
import { provideFeatureFlag } from "./hooks/useFeatureFlag";
import properties from "./properties";
import { Operator } from "@fresh8/types/src";
import { randomCampaign, randomOperator } from "./lib/ramdomise";
import { F8LoadingError } from "./lib/loading";
import { useShouldDisplayOffer } from "./hooks/useDisplayOffer";
import { provideIntersectionObserver } from "./hooks/useIntersectionObserver";
import { CardAnalyticsContext } from "@fresh8/types/src/analytics";
import {
  createCardSequence,
  provideMarketCards
} from "./composition/card-creation";
import { BetData } from "@fresh8/types/src/betData";
import { Brand } from "@fresh8/types";

import {
  provideTemplateSize,
  useTemplateSize
} from "@fresh8/ad-data-composition/src/useTemplateSize";

export default defineComponent({
  name: "AppWrapper",
  components: {
    App,
    Loader
  },
  props: {
    adTag: {
      type: Object as PropType<AdTag>,
      required: true
    }
  },
  setup(props) {
    const adTag = ref(props.adTag);

    const selectedOperator = computed({
      get() {
        return adTag.value.operator._id
          ? adTag.value.operator
          : randomOperator(adTag.value.operators || []);
      },
      set(newValue) {
        adTag.value = {
          ...adTag.value,
          operator: newValue
        } as AdTag;
      }
    });

    const selectedCampaign = computed(() =>
      randomCampaign(
        adTag.value?.campaigns || [],
        adTag.value?.classification,
        selectedOperator.value || ({} as Operator)
      )
    );

    const selectedCampaignPreroll = computed(() => {
      return selectedCampaign.value?.campaignContent?.preroll;
    });

    // get betdata
    const betData = useGetPublisherBetData(
      adTag,
      selectedOperator,
      selectedCampaign
    );

    // when betdata operator changes sync adTag operator
    watch(betData.result, betData => {
      if (!betData) return;
      if (betData.operator?._id !== adTag.value.operator?._id) {
        adTag.value.operator = {
          _id: betData.operator?._id,
          key: betData.operator?.name,
          name: betData.operator?.friendlyName || ""
        };
      }
    });

    const jssContext = createJssContext({});

    const brand = computed(() => adTag.value?.brand);
    const preview = computed(() => ({ isPreview: false } as Preview));
    const tracking = computed(() => adTag.value?.tracking);
    const team = computed(() => brand.value?.team);
    const macros = computed(() => adTag.value?.macros || []);

    const stake = computed(() => {
      let stake = potentialWinningsDefaults.stake;

      if (brand.value?.adTemplate?.potentialWinnings?.stake) {
        stake = brand.value.adTemplate.potentialWinnings.stake;
      }

      return stake;
    });

    const adTagExperiments = computed(() => adTag.value?.experiments);

    /** Experiment selected to show its offer */
    const adTagExperiment = computed(() => {
      // returns an empty object when the experiments don't exist
      if ((adTagExperiments.value?.length || 0) === 0) return;

      // if we have a campaign selected, we randomly choose to show an experiment or not
      const useExperiment = selectedCampaign.value
        ? !!Math.floor(Math.random() * 2)
        : true;

      if (!useExperiment) return;

      // new we filter the valid experiments, and if we have no valid experiments, we return undefined.
      const validExperiments = (adTagExperiments.value || []).filter(e => {
        return !!e.variants[0].operators[selectedOperator.value?._id || ""];
      });

      if (validExperiments.length === 0) return;

      // between all valid experiments we choose one randomly.
      const selectedExperiment =
        validExperiments[Math.floor(Math.random() * validExperiments.length)];

      return selectedExperiment;
    });

    /** represents the selected experiment variant, if we have an experiment at all. */
    const adTagExperimentVariant = computed<AdTagExperimentVariant | undefined>(
      () => {
        if (!adTagExperiment.value) return;

        const selectedVariant =
          adTagExperiment.value.variants[
            Math.floor(Math.random() * adTagExperiment.value.variants.length)
          ];

        return selectedVariant;
      }
    );

    provideFeatureFlag({ isEngineer: false });
    provideJssContext(jssContext);
    const _adTag = provideAdTag(adTag, selectedOperator, selectedCampaign);
    const _betData = provideBetData(
      betData.result,
      tracking,
      team,
      macros,
      stake,
      selectedOperator,
      adTagExperimentVariant,
      selectedCampaign,
      useGetPublisherBetData
    );
    const _brand = provideBrand(brand);

    // if running a card ordering experiment, update the card order on the brand
    watchEffect(() => {
      if (adTagExperiment.value?.type === "cardOrdering") {
        const operatorId = selectedOperator.value?._id || "";

        brand.value.adTemplate.cardOrdering =
          adTagExperimentVariant.value?.operators[operatorId].cardOrdering;
      }
    });

    // if running an offer experiment, update the card order on the brand to include an offer
    watchEffect(() => {
      if (adTagExperiment.value?.type === "offer") {
        if (!brand?.value?.adTemplate?.cardOrdering?.general.includes("offer"))
          brand?.value?.adTemplate?.cardOrdering?.general.unshift("offer");
      }
    });

    providePreview(preview);

    // setup prerolls
    const sizeId = computed(() => adTag?.value?.size?._id);
    const prerollImageSets = computed(() => {
      return selectedCampaignPreroll.value
        ? ({
            default: selectedCampaignPreroll.value?.prerollImageSet
          } as BackgroundImageSetMap)
        : brand.value?.prerollImageSets;
    });
    const brandPreroll = computed(() => brand.value?.adTemplate?.preroll);

    providePreroll(
      brandPreroll,
      sizeId,
      prerollImageSets,
      selectedCampaignPreroll
    );

    const templateSizeContext = useTemplateSize(_adTag);
    provideTemplateSize(templateSizeContext);

    const shouldDisplayOffer = useShouldDisplayOffer(_betData, _brand, _adTag, {
      preview
    });

    const cardSequenceContext = createCardSequence(
      betData.result as Ref<BetData>,
      brand as Ref<Brand>,
      templateSizeContext.size,
      preview as Ref<Preview>,
      {
        displayOfferCard: shouldDisplayOffer,
        insightABTestingEnabled: computed(() =>
          !brand.value?.adTemplate?.insights?.enabledAbTesting
            ? true
            : Math.random() < 0.5
        )
      }
    );

    provideMarketCards(cardSequenceContext);

    const analyticsContext = createAnalyticsContext(
      betData.result,
      adTag,
      properties.statsURL,
      cardSequenceContext.cards,
      preview,
      adTagExperiment,
      adTagExperimentVariant,
      selectedCampaign
    );
    provideAnalyticsContext(analyticsContext);

    const cardAnalyticsContext = createCardAnalyticsContext(
      betData.result,
      adTag,
      properties.cardAnalyticsURL,
      selectedCampaign,
      adTagExperiment,
      adTagExperimentVariant
    );
    provideCardAnalyticsContext(
      cardAnalyticsContext as Ref<CardAnalyticsContext>
    );

    provideTheme(ref({}));

    provideIntersectionObserver();

    const loaded = computed(() => !betData.loading.value);

    return {
      loaded,
      canDisplay: computed(() => !!adTag.value?.brand),
      displayContent: computed(() => {
        if (
          betData.loading.value ||
          betData.error.value ||
          !betData.result.value
        ) {
          if (shouldDisplayOffer) {
            return true;
          }

          F8LoadingError();
          return false;
        }

        return true;
      })
    };
  }
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
